import React, { useState, useEffect } from 'react';
import { 
  Users, Plus, Edit2, Trash2, Search, X, BookOpen, Loader2
} from 'lucide-react';
import { groupsAPI, subjectsAPI } from '../services/api';
import Pagination from '../components/Pagination';

const GroupsPage = () => {
  const [groups, setGroups] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showSubjectsModal, setShowSubjectsModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [formData, setFormData] = useState({ name: '', course: 1, subject_ids: [] });
  const [searchTerm, setSearchTerm] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(20);

  useEffect(() => {
    fetchGroups();
    fetchSubjects();
  }, [currentPage]);

  // Qidiruv o'zgarganda birinchi sahifaga qaytish
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const fetchGroups = async () => {
    try {
      setLoading(true);
      const response = await groupsAPI.getAll({ page: currentPage });
      const data = response.data;
      
      if (data.results) {
        setGroups(data.results);
        setTotalCount(data.count || 0);
      } else if (Array.isArray(data)) {
        setGroups(data);
        setTotalCount(data.length);
      } else {
        setGroups([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Guruhlarni yuklashda xato:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await subjectsAPI.getSimple();
      setSubjects(response.data.subjects || []);
    } catch (error) {
      console.error('Fanlarni yuklashda xato:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingGroup) {
        await groupsAPI.update(editingGroup.id, formData);
      } else {
        await groupsAPI.create(formData);
      }
      fetchGroups();
      closeModal();
    } catch (error) {
      console.error('Saqlashda xato:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Guruhni o'chirishni tasdiqlaysizmi?")) {
      try {
        await groupsAPI.delete(id);
        fetchGroups();
      } catch (error) {
        console.error("O'chirishda xato:", error);
      }
    }
  };

  const handleAssignSubjects = async (subjectIds) => {
    try {
      await groupsAPI.assignSubjects(selectedGroup.id, subjectIds);
      fetchGroups();
      setShowSubjectsModal(false);
    } catch (error) {
      console.error("Fanlarni biriktirishda xato:", error);
    }
  };

  const openModal = (group = null) => {
    if (group) {
      setEditingGroup(group);
      setFormData({ 
        name: group.name, 
        course: group.course,
        subject_ids: group.subjects?.map(s => s.id) || []
      });
    } else {
      setEditingGroup(null);
      setFormData({ name: '', course: 1, subject_ids: [] });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingGroup(null);
    setFormData({ name: '', course: 1, subject_ids: [] });
  };

  const openSubjectsModal = (group) => {
    setSelectedGroup(group);
    setShowSubjectsModal(true);
  };

  // Pagination hisoblash
  const totalPages = Math.ceil(totalCount / pageSize);
  const startIndex = (currentPage - 1) * pageSize;

  // Frontend filtering (faqat joriy sahifadagi ma'lumotlar uchun)
  const filteredGroups = groups.filter(group => 
    group.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="loading-container">
        <Loader2 className="spinner" size={40} />
        <p>Yuklanmoqda...</p>
      </div>
    );
  }

  return (
    <div className="page-container">
      <header className="page-header">
        <div className="page-title">
          <h1><Users size={28} /> Guruhlar</h1>
          <p>Barcha guruhlar ro'yxati</p>
        </div>
        <button className="btn btn-primary" onClick={() => openModal()}>
          <Plus size={20} /> Yangi guruh
        </button>
      </header>

      {/* Search */}
      <div className="search-bar">
        <Search size={20} />
        <input 
          type="text" 
          placeholder="Guruhni qidirish..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Stats */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '16px',
        padding: '12px 16px',
        background: '#f8fafc',
        borderRadius: '8px'
      }}>
        <span style={{ color: '#64748b' }}>
          Jami: <strong style={{ color: '#1e293b' }}>{totalCount}</strong> ta guruh
        </span>
        {totalPages > 1 && (
          <span style={{ color: '#64748b' }}>
            Sahifa: <strong style={{ color: '#1e293b' }}>{currentPage}</strong> / {totalPages}
          </span>
        )}
      </div>

      {/* Groups Table */}
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>№</th>
              <th>Guruh nomi</th>
              <th>Kurs</th>
              <th>Talabalar</th>
              <th>Fanlar</th>
              <th>Amallar</th>
            </tr>
          </thead>
          <tbody>
            {filteredGroups.map((group, index) => (
              <tr key={group.id}>
                <td>{startIndex + index + 1}</td>
                <td><strong>{group.name}</strong></td>
                <td>
                  <span className="badge badge-info">{group.course}-kurs</span>
                </td>
                <td>{group.students_count} ta</td>
                <td>
                  <button 
                    className="btn btn-sm btn-outline"
                    onClick={() => openSubjectsModal(group)}
                  >
                    <BookOpen size={16} />
                    {group.subjects?.length || 0} ta fan
                  </button>
                </td>
                <td>
                  <div className="action-buttons">
                    <button className="btn-icon edit" onClick={() => openModal(group)}>
                      <Edit2 size={18} />
                    </button>
                    <button className="btn-icon delete" onClick={() => handleDelete(group.id)}>
                      <Trash2 size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredGroups.length === 0 && (
          <div className="empty-state">
            <Users size={48} />
            <p>Guruhlar topilmadi</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalCount={totalCount}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
      />

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingGroup ? "Guruhni tahrirlash" : "Yangi guruh"}</h2>
              <button className="btn-close" onClick={closeModal}>
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Guruh nomi</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Masalan: 101-guruh"
                  required
                />
              </div>
              <div className="form-group">
                <label>Kurs</label>
                <select
                  value={formData.course}
                  onChange={(e) => setFormData({...formData, course: parseInt(e.target.value)})}
                >
                  <option value={1}>1-kurs</option>
                  <option value={2}>2-kurs</option>
                  <option value={3}>3-kurs</option>
                  <option value={4}>4-kurs</option>
                </select>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                  Bekor qilish
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingGroup ? "Saqlash" : "Yaratish"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Subjects Assignment Modal */}
      {showSubjectsModal && selectedGroup && (
        <SubjectsModal
          group={selectedGroup}
          subjects={subjects}
          onSave={handleAssignSubjects}
          onClose={() => setShowSubjectsModal(false)}
        />
      )}
    </div>
  );
};

// Fanlarni biriktirish modali
const SubjectsModal = ({ group, subjects, onSave, onClose }) => {
  const [selectedIds, setSelectedIds] = useState(group.subjects?.map(s => s.id) || []);

  const toggleSubject = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{group.name} - Fanlar</h2>
          <button className="btn-close" onClick={onClose}>
            <X size={24} />
          </button>
        </div>
        <div className="subjects-list">
          {subjects.length === 0 ? (
            <p className="text-muted">Fanlar mavjud emas. Avval fanlar yarating.</p>
          ) : (
            subjects.map(subject => (
              <label key={subject.id} className="checkbox-item">
                <input
                  type="checkbox"
                  checked={selectedIds.includes(subject.id)}
                  onChange={() => toggleSubject(subject.id)}
                />
                <span>{subject.name}</span>
              </label>
            ))
          )}
        </div>
        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onClose}>
            Bekor qilish
          </button>
          <button className="btn btn-primary" onClick={() => onSave(selectedIds)}>
            Saqlash
          </button>
        </div>
      </div>
    </div>
  );
};

export default GroupsPage;
